/**
 * WC-GCAL — Google Calendar inbound (Layer A bell rows).
 */
export const GOOGLE_CALENDAR_INBOUND_KIND = "google_calendar_inbound";

export const GOOGLE_CALENDAR_READONLY_SCOPE = "https://www.googleapis.com/auth/calendar.readonly";

export type GoogleCalendarInboundEvent = {
  google_event_id: string;
  title: string;
  start_at: string;
  end_at: string | null;
  html_link: string | null;
  all_day: boolean;
};

export type GoogleCalendarLinkStatus = {
  linked: boolean;
  google_email: string | null;
  selected_calendar_id: string;
  sync_enabled: boolean;
  last_synced_at: string | null;
  last_sync_error: string | null;
  oauth_available: boolean;
};

const GCAL_REF_PREFIX = "[gcal_ref:";

export function googleEventRefMarker(googleEventId: string): string {
  return `${GCAL_REF_PREFIX}${googleEventId.trim()}]`;
}

export function parseGoogleEventRefFromBody(body: string): string | null {
  const m = /^\[gcal_ref:([^\]]+)\]/.exec(String(body || "").trim());
  return m?.[1]?.trim() || null;
}

export function buildGoogleCalendarNotificationBody(event: GoogleCalendarInboundEvent): string {
  const ref = googleEventRefMarker(event.google_event_id);
  const when = event.all_day
    ? `All day · ${new Date(event.start_at).toLocaleDateString()}`
    : `${new Date(event.start_at).toLocaleString()}${event.end_at ? ` – ${new Date(event.end_at).toLocaleTimeString()}` : ""}`;
  const linkLine = event.html_link ? `\nOpen in Google Calendar: ${event.html_link}` : "";
  return `${ref}\n${when}${linkLine}`.trim();
}

/** UTC window: start of local calendar day through end of next calendar day (pilot: today + buffer). */
export function googleCalendarSyncWindow(nowMs: number = Date.now()): { timeMin: string; timeMax: string } {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  const timeMin = d.toISOString();
  const end = new Date(d);
  end.setDate(end.getDate() + 2);
  end.setHours(23, 59, 59, 999);
  return { timeMin, timeMax: end.toISOString() };
}

export function isInboundGoogleEventToday(event: GoogleCalendarInboundEvent, nowMs: number = Date.now()): boolean {
  const startMs = Date.parse(event.start_at);
  if (Number.isNaN(startMs)) return false;
  const dayStart = new Date(nowMs);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);
  return startMs >= dayStart.getTime() && startMs <= dayEnd.getTime();
}

export function filterInboundGoogleEvents(input: {
  events: readonly GoogleCalendarInboundEvent[];
  turboOriginIds: ReadonlySet<string>;
}): GoogleCalendarInboundEvent[] {
  const out: GoogleCalendarInboundEvent[] = [];
  const seen = new Set<string>();
  for (const ev of input.events) {
    const id = ev.google_event_id.trim();
    if (!id || seen.has(id)) continue;
    if (input.turboOriginIds.has(id)) continue;
    seen.add(id);
    out.push(ev);
  }
  return out.sort((a, b) => Date.parse(a.start_at) - Date.parse(b.start_at));
}

export function parseGoogleCalendarApiEvent(raw: Record<string, unknown>): GoogleCalendarInboundEvent | null {
  const id = typeof raw.id === "string" ? raw.id.trim() : "";
  if (!id || raw.status === "cancelled") return null;
  const summary = typeof raw.summary === "string" && raw.summary.trim() ? raw.summary.trim() : "(No title)";
  const startObj = raw.start && typeof raw.start === "object" ? (raw.start as Record<string, unknown>) : null;
  const endObj = raw.end && typeof raw.end === "object" ? (raw.end as Record<string, unknown>) : null;
  const dateOnly = typeof startObj?.date === "string" ? startObj.date : null;
  const dateTime = typeof startObj?.dateTime === "string" ? startObj.dateTime : null;
  const start_at = dateTime || (dateOnly ? `${dateOnly}T12:00:00.000Z` : "");
  if (!start_at) return null;
  const endDateTime = typeof endObj?.dateTime === "string" ? endObj.dateTime : null;
  const endDateOnly = typeof endObj?.date === "string" ? endObj.date : null;
  const end_at = endDateTime || (endDateOnly ? `${endDateOnly}T12:00:00.000Z` : null);
  const html_link = typeof raw.htmlLink === "string" ? raw.htmlLink : null;
  return {
    google_event_id: id,
    title: summary.slice(0, 500),
    start_at,
    end_at,
    html_link,
    all_day: Boolean(dateOnly && !dateTime),
  };
}

export function extractTurboGoogleEventIdsFromLeadData(data: unknown): string[] {
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];
  const followUps = (data as { follow_ups?: unknown }).follow_ups;
  if (!Array.isArray(followUps)) return [];
  const out: string[] = [];
  for (const fu of followUps) {
    if (!fu || typeof fu !== "object") continue;
    const gid = (fu as { google_event_id?: string }).google_event_id;
    if (typeof gid === "string" && gid.trim()) out.push(gid.trim());
  }
  return out;
}
